import React from 'react';
import { useVendorColors } from '../hooks/useVendorColors';
import { selectVendorIndicator } from '../hooks/useSpendIndicators';
import SpendDrillButton from '../components/SpendDrillButton';
import './OverlayCoverageList.css';

/**
 * Compact renderer for a cell's vendor-overlay coverages. Used inside
 * capability/signal cards when one or more vendor overlays are active.
 *
 * Renders up to `maxVisible` rows of [colored-dot] [product name]; any
 * extras collapse into "+ N more". Each row carries `data-coverage-level`
 * so CSS can differentiate STRONG / PARTIAL / GAP per row.
 *
 * The enclosing button's `data-overlay-count` drives cell-level styling
 * (single-vendor full highlight vs multi-vendor neutral bg + dots).
 *
 * Props:
 *   coverages — sorted array from `buildCoverageMap[capId]`
 *   maxVisible — default 2; cells with room can increase
 *   position — 'bottom' (default) renders with a top margin separating it
 *     from content above; 'top' reverses to a bottom margin so it visually
 *     attaches to the capability content below (used when the list is
 *     rendered ABOVE the capability name).
 */
export default function OverlayCoverageList({
  coverages = [],
  maxVisible = 2,
  position = 'bottom',
  spendIndicators = null,
  onDrillIn = null,
}) {
  const { colorFor } = useVendorColors();

  if (!coverages || coverages.length === 0) return null;

  const visible = coverages.slice(0, maxVisible);
  const hidden = coverages.slice(maxVisible);

  const moreTooltip = hidden
    .map(c => `${c.vendor.name}: ${c.productName} (${c.coverageLevel})`)
    .join('\n');

  return (
    <div className={`zta-ocl zta-ocl--${position}`}>
      {visible.map(c => {
        const vendorIndicator = selectVendorIndicator(
          spendIndicators,
          c.vendor?.name,
          c.productName
        );
        return (
          <div
            key={c.vendor.id}
            className="zta-ocl-row"
            data-coverage-level={c.coverageLevel}
            title={`${c.vendor.name}: ${c.productName}${c.coverageLevel ? ` (${c.coverageLevel})` : ''}${c.notes ? ` — ${c.notes}` : ''}`}
          >
            <span
              className="zta-ocl-dot"
              style={{ background: colorFor(c.vendor) }}
            />
            <span className="zta-ocl-product">{c.productName}</span>
            {vendorIndicator && (
              <SpendDrillButton
                indicator={vendorIndicator}
                onClick={() => onDrillIn?.({
                  vendor: c.vendor?.name,
                  product: c.productName,
                })}
                label={`${c.vendor?.name || 'vendor'} transactions`}
                size={10}
                className="zta-spend-drill--tiny"
              />
            )}
          </div>
        );
      })}
      {hidden.length > 0 && (
        <div className="zta-ocl-row zta-ocl-row--more" title={moreTooltip}>
          + {hidden.length} more
        </div>
      )}
    </div>
  );
}
